import { useTokenCredentials } from '@state/useTokenCredentials';

/**
 * Hard timeout (ms) applied to every request fired through {@link hitAPI}.
 *
 * A hung connection is aborted after this window so the caller fails
 * deterministically instead of waiting on the OS-level TCP timeout (which
 * can be minutes). Sized generously for slow mobile networks on a
 * transaction POST.
 */
const REQUEST_TIMEOUT_MS = 30000;

/**
 * React hook returning an authenticated `fetch` wrapper.
 *
 * The wrapper attaches JSON headers, optionally a bearer token, enforces a
 * request timeout, and transparently refreshes the access token on HTTP 401.
 *
 * @returns {(url: string, method: string, data?: object, token?: boolean)
 *   => Promise<object|undefined>} The configured `hitAPI` function.
 */
const useHITApi = () => {
    const { getTokenCredentials, updateTokenCredentials } = useTokenCredentials((state) => ({
        getTokenCredentials: state.getTokenCredentials,
        updateTokenCredentials: state.updateTokenCredentials,
    }));

    const REFRESH_TOKEN = import.meta.env[
        `VITE_REACT_APP_REFRESH_TOKEN_${import.meta.env.MODE}`
    ];

    /**
     * Perform an HTTP request with a hard timeout and explicit failure
     * semantics.
     *
     * Return contract — consumed by the retry loop in
     * `LambdaFunctionContext.promiseHitTransaction`:
     *  - **Success (HTTP 2xx):** resolved JSON body of the response (truthy).
     *  - **HTTP 4xx/5xx:** structured `{ error: true, status, message }`
     *    (truthy) — the retry loop stops and the error surfaces to the caller.
     *  - **Network failure / timeout / abort / unparseable body:**
     *    `undefined` (falsy) — the retry loop continues. Re-sending is safe
     *    because every transaction POST now carries an idempotent
     *    `transaction_uuid`, so a replay resolves to the original transaction
     *    on the Lambda side instead of creating a duplicate.
     *
     * HTTP 401 is handled separately: the token is refreshed and the original
     * 401 response body is returned, preserving prior behaviour.
     *
     * @param {string} url - Endpoint URL (whitespace is stripped).
     * @param {string} method - HTTP method; defaults to `'GET'`.
     * @param {object} [data] - Request body; JSON-serialised when present.
     * @param {boolean} [token] - When true, attaches the bearer access token.
     * @returns {Promise<object|undefined>} See the return contract above.
     */
    const hitAPI = async (url, method, data, token) => {
        const newUrl = url.replace(/\s/g, '');
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        let feedback;
        try {
            const header = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            };

            if (token && getTokenCredentials().access_token) {
                header['Authorization'] = getTokenCredentials().access_token;
            }

            feedback = await fetch(newUrl, {
                method: method || 'GET',
                headers: header,
                body: data ? JSON.stringify(data) : '',
                signal: controller.signal,
            });

            // HTTP 401 — refresh the access token and surface the original
            // response body, preserving prior behaviour.
            if (feedback.status === 401) {
                const response = await feedback.json();
                const refresh = await hitAPI(REFRESH_TOKEN, 'POST', {
                    refresh_token: getTokenCredentials().refresh_token,
                });
                if (refresh?.result) {
                    updateTokenCredentials(refresh.result);
                }
                return response;
            }

            // Any other non-2xx — return a structured error (truthy) so the
            // retry loop stops and the failure is surfaced to the caller.
            if (!feedback.ok) {
                let message = `HTTP ${feedback.status}`;
                try {
                    const errorBody = await feedback.json();
                    message =
                        errorBody?.messages?.[0] || errorBody?.message || message;
                } catch (parseError) {
                    // Non-JSON error body — keep the generic status message.
                }
                return { error: true, status: feedback.status, message };
            }

            return await feedback.json();
        } catch (error) {
            // Network failure, request timeout/abort, or an unparseable body
            // on an otherwise-OK response. Return undefined (falsy) so the
            // transaction retry loop continues — safe now that every POST is
            // idempotent via `transaction_uuid`.
            console.log(error);
            return undefined;
        } finally {
            clearTimeout(timeoutId);
        }
    };

    return hitAPI;
};

export default useHITApi;
